/**
 * AI Search memory, in this browser only.
 *
 * - The LAST search of this tab (sessionStorage): coming back to the page from
 *   an asset or a folder restores it, and the results come out of the query
 *   cache instead of running the slow search again.
 * - RECENT searches (localStorage, per user): a short list to jump back to.
 *
 * Storage can be unavailable (private mode, blocked site data), so every access
 * is guarded and the page works the same without it, just with no memory.
 */
export type SearchFilters = Record<string, string | string[]>;
export type RecentSearch = { filters: SearchFilters; total: number; at: number };

const LAST_KEY = 'ecomstat-dam-ai-search-last';
const MAX_RECENT = 8;
const recentKey = (userId: number) => `ecomstat-dam-ai-search-recent-${userId}`;

function read<T>(storage: () => Storage, key: string): T | null {
  try {
    const raw = storage().getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function write(storage: () => Storage, key: string, value: unknown) {
  try {
    storage().setItem(key, JSON.stringify(value));
  } catch {
    /* no storage: no memory, nothing else changes */
  }
}

export const loadLastSearch = () => read<SearchFilters>(() => sessionStorage, LAST_KEY);
export const saveLastSearch = (filters: SearchFilters) => write(() => sessionStorage, LAST_KEY, filters);

export function loadRecentSearches(userId: number): RecentSearch[] {
  const list = read<RecentSearch[]>(() => localStorage, recentKey(userId));
  return Array.isArray(list) ? list.filter((r) => r && typeof r.filters === 'object') : [];
}

export function clearRecentSearches(userId: number) {
  try {
    localStorage.removeItem(recentKey(userId));
  } catch {
    /* ignore */
  }
}

export const sameSearch = (a: SearchFilters, b: SearchFilters) => JSON.stringify(normalise(a)) === JSON.stringify(normalise(b));

/**
 * Adds a search at the top. A search that only extends or trims the newest one
 * ("clip" → "clipper") replaces it, so typing does not fill the list with every
 * prefix on the way to the real query.
 */
export function pushRecentSearch(userId: number, filters: SearchFilters, total: number): RecentSearch[] {
  const entry: RecentSearch = { filters: normalise(filters), total, at: Date.now() };
  let list = loadRecentSearches(userId).filter((r) => !sameSearch(r.filters, entry.filters));
  if (list[0] && isRefinement(list[0].filters, entry.filters)) list = list.slice(1);
  list = [entry, ...list].slice(0, MAX_RECENT);
  write(() => localStorage, recentKey(userId), list);
  return list;
}

/** Trimmed strings, sorted arrays and no empty fields, so equal searches compare equal. */
function normalise(f: SearchFilters): SearchFilters {
  const out: SearchFilters = {};
  for (const k of Object.keys(f).sort()) {
    const v = f[k];
    if (Array.isArray(v)) {
      if (v.length) out[k] = [...v].sort();
    } else if (typeof v === 'string' && v.trim()) {
      out[k] = v.trim();
    }
  }
  return out;
}

function isRefinement(a: SearchFilters, b: SearchFilters): boolean {
  const na = normalise(a);
  const nb = normalise(b);
  for (const k of new Set([...Object.keys(na), ...Object.keys(nb)])) {
    const x = na[k] ?? '';
    const y = nb[k] ?? '';
    if (Array.isArray(x) || Array.isArray(y)) {
      if (JSON.stringify(x) !== JSON.stringify(y)) return false;
    } else if (!(x.toLowerCase().startsWith(y.toLowerCase()) || y.toLowerCase().startsWith(x.toLowerCase()))) {
      return false;
    }
  }
  return true;
}
